import { createSagaActions } from "./utils";
import { createReducer, createAction } from "redux-act";
import { takeLatest, put } from "redux-saga/effects";
import {
  getHolidayBookingsAPI,
  getHolidayApprovalsAPI,
  getHolidayCalendarAPI,
  getHolidayAdminListAPI,
  deleteHolidayAPI,
  bookHolidayAPI,
  approveHolidayAPI,
  declineHolidayAPI,
  cancelHolidayAPI,
} from "../../crud/users.crud";
import { apiErrorMessage } from "../../crud/helper";
import { getMe } from "./app.duck";

import { GetAccessToken } from "./accessToken";

export const getHolidayPage = createAction("holiday/GET_HOLIDAY_PAGE");

export const getMyBookings = createSagaActions("holiday/GET_BOOKINGS");
export const getApprovals = createSagaActions("holiday/GET_APPROVALS");
export const bookHoliday = createSagaActions("holiday/BOOK_HOLIDAY");
export const resetHolidayBooking = createAction("holiday/RESET_BOOKING");
export const clearHolidayActionError = createAction(
  "holiday/CLEAR_ACTION_ERROR"
);
export const approveHoliday = createSagaActions("holiday/APPROVE_HOLIDAY");
export const declineHoliday = createSagaActions("holiday/DECLINE_HOLIDAY");
export const cancelHoliday = createSagaActions("holiday/CANCEL_HOLIDAY");
export const getHolidayCalendar = createSagaActions("holiday/GET_CALENDAR");
export const getAdminHolidayList = createSagaActions("holiday/GET_ADMIN");
export const deleteHoliday = createSagaActions("holiday/DELETE");

const initialState = {
  loading: false,
  bookingStatus: "idle",
  bookingError: "",
  // Why the last cancel, approve, decline or delete failed.
  actionError: "",
  holidayCalendar: [],
  myBookings: [],
  approvals: [],
  adminList: [],
};

const startAction = (state) => ({ ...state, loading: true, actionError: "" });
const finishAction = (state) => ({ ...state, loading: false });
const failAction = (state, message) => ({
  ...state,
  loading: false,
  actionError: message,
});

export const reducer = createReducer(
  {
    [getMyBookings]: (state) => ({ ...state, loading: true }),
    [getMyBookings.done]: (state, data) => ({
      ...state,
      loading: false,
      myBookings: data,
    }),
    [getMyBookings.fail]: (state) => ({ ...state, loading: false }),
    [getApprovals]: (state) => ({ ...state, loading: true }),
    [getApprovals.done]: (state, data) => ({
      ...state,
      loading: false,
      approvals: data,
    }),
    [getApprovals.fail]: (state) => ({ ...state, loading: false }),
    [getAdminHolidayList]: (state) => ({ ...state, loading: true }),
    [getAdminHolidayList.done]: (state, data) => ({
      ...state,
      loading: false,
      adminList: data,
    }),
    [getAdminHolidayList.fail]: (state) => ({ ...state, loading: false }),

    [getHolidayCalendar]: (state) => ({ ...state, loading: true }),
    [getHolidayCalendar.done]: (state, data) => ({
      ...state,
      loading: false,
      holidayCalendar: data,
    }),
    [getHolidayCalendar.fail]: (state) => ({ ...state, loading: false }),
    [resetHolidayBooking]: (state) => ({
      ...state,
      bookingStatus: "idle",
      bookingError: "",
    }),
    [bookHoliday]: (state) => ({
      ...state,
      bookingStatus: "saving",
      bookingError: "",
    }),
    [bookHoliday.done]: (state) => ({
      ...state,
      bookingStatus: "saved",
      bookingError: "",
    }),
    [bookHoliday.fail]: (state, message) => ({
      ...state,
      bookingStatus: "failed",
      bookingError: message,
    }),
    [cancelHoliday]: startAction,
    [cancelHoliday.done]: finishAction,
    [cancelHoliday.fail]: failAction,
    [approveHoliday]: startAction,
    [approveHoliday.done]: finishAction,
    [approveHoliday.fail]: failAction,
    [declineHoliday]: startAction,
    [declineHoliday.done]: finishAction,
    [declineHoliday.fail]: failAction,
    [deleteHoliday]: startAction,
    [deleteHoliday.done]: finishAction,
    [deleteHoliday.fail]: failAction,
    [clearHolidayActionError]: (state) => ({ ...state, actionError: "" }),
    [getHolidayPage]: (state) => ({ ...state, actionError: "" }),
  },
  initialState
);

export function* saga() {
  yield takeLatest(getMyBookings, function* getMyBookingsSaga() {
    try {
      const token = yield* GetAccessToken();
      const result = yield getHolidayBookingsAPI(token);
      yield put(getMyBookings.done(result.data));
    } catch (error) {
      console.log(error);
      yield put(getMyBookings.fail());
    }
  });
  yield takeLatest(getApprovals, function* getAprrovalsSaga() {
    try {
      const token = yield* GetAccessToken();
      const result = yield getHolidayApprovalsAPI(token);
      yield put(getApprovals.done(result.data));
    } catch (error) {
      console.log(error);
      yield put(getApprovals.fail());
    }
  });
  yield takeLatest(getAdminHolidayList, function* getAdminHolidayListSaga() {
    try {
      const token = yield* GetAccessToken();
      const result = yield getHolidayAdminListAPI(token);
      yield put(getAdminHolidayList.done(result.data));
    } catch (error) {
      console.log(error);
      yield put(getAdminHolidayList.fail());
    }
  });
  yield takeLatest(getHolidayCalendar, function* getHolidayCalendarSaga() {
    try {
      const token = yield* GetAccessToken();
      const result = yield getHolidayCalendarAPI(token);
      const mapped = result.data.map((h) => ({
        ...h,
        start: new Date(h.start),
        end: new Date(h.end),
      }));
      yield put(getHolidayCalendar.done(mapped));
    } catch (error) {
      console.log(error);
      yield put(getHolidayCalendar.fail());
    }
  });
  yield takeLatest(bookHoliday, function* bookHolidaySaga({ payload: data }) {
    try {
      const token = yield* GetAccessToken();
      const result = yield bookHolidayAPI(token, data);
      if (!result.data)
        throw new Error("The holiday could not be booked. Please try again.");
      yield put(bookHoliday.done());
      yield put(getMyBookings());
      yield put(getApprovals());
      yield put(getHolidayCalendar());
      yield put(getMe());
      //if I just booked holiday for someone else
      if (data.user) {
        yield put(getAdminHolidayList());
      }
    } catch (error) {
      yield put(
        bookHoliday.fail(
          apiErrorMessage(
            error,
            "The holiday could not be booked. Please try again."
          )
        )
      );
    }
  });
  yield takeLatest(cancelHoliday, function* cancelHolidaySaga({
    payload: { bookingId },
  }) {
    try {
      const token = yield* GetAccessToken();
      yield cancelHolidayAPI(token, bookingId);
      yield put(cancelHoliday.done());
    } catch (error) {
      yield put(
        cancelHoliday.fail(
          apiErrorMessage(error, "The booking could not be cancelled.")
        )
      );
    }
    // Refresh either way: a failure may mean the booking had already changed.
    yield put(getMyBookings());
    yield put(getHolidayCalendar());
    yield put(getMe());
  });
  yield takeLatest(approveHoliday, function* approveHolidaySaga({
    payload: { bookingId },
  }) {
    try {
      const token = yield* GetAccessToken();
      yield approveHolidayAPI(token, bookingId);
      yield put(approveHoliday.done());
    } catch (error) {
      yield put(
        approveHoliday.fail(
          apiErrorMessage(error, "The booking could not be approved.")
        )
      );
    }
    yield put(getApprovals());
    yield put(getHolidayCalendar());
  });
  yield takeLatest(declineHoliday, function* declineHolidaySaga({
    payload: { bookingId, note },
  }) {
    try {
      const token = yield* GetAccessToken();
      yield declineHolidayAPI(token, bookingId, note);
      yield put(declineHoliday.done());
    } catch (error) {
      yield put(
        declineHoliday.fail(
          apiErrorMessage(error, "The booking could not be declined.")
        )
      );
    }
    yield put(getApprovals());
    yield put(getHolidayCalendar());
  });
  yield takeLatest(deleteHoliday, function* deleteHolidaySaga({
    payload: { bookingId },
  }) {
    try {
      const token = yield* GetAccessToken();
      yield deleteHolidayAPI(token, bookingId);
      yield put(deleteHoliday.done());
    } catch (error) {
      yield put(
        deleteHoliday.fail(
          apiErrorMessage(error, "The booking could not be deleted.")
        )
      );
    }
    // An admin may have deleted their own booking.
    yield put(getAdminHolidayList());
    yield put(getMyBookings());
    yield put(getHolidayCalendar());
    yield put(getMe());
  });

  yield takeLatest(getHolidayPage, function* getHolidayPageSaga({
    payload: { hasHolidayAdminPermission },
  }) {
    yield put(getMe());
    yield put(getApprovals());
    yield put(getMyBookings());
    yield put(getHolidayCalendar());
    if (hasHolidayAdminPermission) {
      yield put(getAdminHolidayList());
    }
  });
}
